import React, { forwardRef } from 'react';
import { Button as Btn } from 'antd';
import { Spin } from '../spin/spin';
import classNames from 'classnames';
/**
 * A custom Button component that extends Ant Design's Button.
 * It allows for an optional right-aligned icon.
 * 
 * {@link https://ant.design/components/button#api Ant Design Button}
 *
 * @module Button
 * @param {Object} props - The component props.
 * @param {ReactNode} [props.icon] - Icon to display on the button.
 * @param {ReactNode} [props.rightIcon] - Icon to display on the right side of the button.
 * @param {string} [props.successText] - Text to display in button when showSuccess is called.
 * @param {string} [props.loading] - loading state of button.
 * @param {boolean} [props.hideSpin] - hideSpin state of button.
 * @param {string} [props.loadingText] - Text to display in button while loading=true.
 * @param {string} [props.className] - custom class name for the button
 * @param {ReactNode} props.children - The content of the button.
 * @param {React.Ref} ref - The ref to be forwarded to the button element.
 * @returns {React.ReactElement} The rendered Button component.
 */

const Button = forwardRef( ( props, ref ) => {
    const { rightIcon, loading, children, loadingText, hideSpin, className, ...restProps } = props;
    const buttonClasses = classNames(
      {
        [ className ]: className,
        'ant-btn-loading': loading && hideSpin ? true : false
      },
    );

    return rightIcon ? (
        <Btn { ...restProps } className={ buttonClasses } ref={ ref } loading={ loading && !hideSpin ? { icon: <Spin className="button-spin" size="mini" /> } : loading && hideSpin ? null : false }>
            { loading && loadingText ? loadingText : children }
            <span className='ant-btn-icon ant-btn-icon-right'>{ rightIcon }</span>
        </Btn>
    ) : (
        <Btn { ...restProps } className={ buttonClasses } loading={ loading && !hideSpin ? { icon: <Spin className="button-spin" size="mini" /> } : loading && hideSpin ? null : false } ref={ ref }>
            { loading && loadingText ? loadingText : children }
        </Btn>
    );
} );

export { Button };
